<script lang="ts" setup>
const { t } = useI18n()
const { modals } = useOverlays()
const enrichmentStore = useEnrichmentStore()

// Template refs
const dropzoneRef = useTemplateRef<HTMLDivElement>('dropzoneRef')

watch(
  () => modals.listEnrichment.opened,
  value => !value && enrichmentStore.reset(),
)

onMounted(() => enrichmentStore.addDropzoneTarget(dropzoneRef.value!))

onUnmounted(() => enrichmentStore.removeDropzoneTarget(dropzoneRef.value!))
</script>

<template>
  <AnimatedBorder
    class="mt-6"
    :spread="enrichmentStore.isDroppingFile ? 16 : 8"
  >
    <div
      ref="dropzoneRef"
      class="gradient-texture-full flex size-full h-[calc(100vh-24rem)] min-h-80 flex-col items-center justify-center rounded-xl border border-gray-100 px-8 py-10 text-center shadow-inner transition duration-300 dark:border-gray-900"
      :class="{
        '!border-gray-700': enrichmentStore.fileToImport && !enrichmentStore.formatError,
        '!border-white dark:!border-gray-900': enrichmentStore.isDroppingFile,
        '!border-red-700': enrichmentStore.formatError,
      }"
    >
      <label
        class="flex size-full cursor-pointer flex-col items-center justify-center"
        @click="enrichmentStore.handleInputClick"
      >
        <div
          v-if="!enrichmentStore.initializing"
          class="-mt-6"
        >
          <BasePower
            class="mb-4"
            :power="enrichmentStore.formatError ? 'dead' : enrichmentStore.fileToImport ? 'magic' : 'welcome'"
          />

          <p class="mb-1.5 font-semibold md:text-lg">
            <template v-if="enrichmentStore.formatError">
              {{ t("formatError", enrichmentStore.dragFilesCount) }}
            </template>

            <template v-else-if="enrichmentStore.fileToImport">
              {{ t("magic") }}
            </template>

            <template v-else>
              {{ t("instructions") }}
            </template>
          </p>

          <p
            v-if="enrichmentStore.formatError || !enrichmentStore.fileToImport"
            class="text-gray-600 dark:text-gray-400"
          >
            <span>
              {{ enrichmentStore.formattedFormats?.length ? t("formats", { formats: enrichmentStore.formattedFormats }) : t("allFormatsAllowed") }}
            </span>

            <br>

            <span v-if="enrichmentStore.maxCompanies">
              {{ t("maxRows", { maxRows: numbro(enrichmentStore.maxCompanies) }) }}
            </span>
          </p>

          <p
            v-else
            class="mt-1 flex flex-col items-center"
          >
            <span class="mb-3 break-all">
              {{ enrichmentStore.fileToImport?.name }}
            </span>

            <UButton
              color="red"
              :icon="getIcon('trash')"
              :label="t('removeFile', 1)"
              variant="subtle"
              @click.prevent="enrichmentStore.reset()"
            />
          </p>
        </div>

        <div
          v-else
          class="flex flex-col items-center justify-center"
        >
          <BaseSpinner class="mb-4 size-8" />

          <span class="text-lg font-semibold">{{ t('loadingFile') }}</span>
        </div>
      </label>
    </div>
  </AnimatedBorder>
</template>

<i18n lang="json">
{
  "en": {
    "allFormatsAllowed": "All formats are allowed",
    "formatError": "The format for this file is not allowed | The formats for these files are not allowed",
    "formats": "Allowed formats: {formats}",
    "instructions": "Click here or drop your files",
    "loadingFile": "Loading file...",
    "magic": "Let's make some magic happen!",
    "maxRows": "Max rows: {maxRows}",
    "removeFile": "Remove File | Remove Files"
  },
  "es": {
    "allFormatsAllowed": "Todos los formatos son permitidos",
    "formatError": "El formato de este archivo no es permitido | Los formatos de estos archivos no son permitidos",
    "formats": "Formatos permitidos: {formats}",
    "instructions": "Haz clic aquí o suelta tus archivos",
    "loadingFile": "Cargando archivo...",
    "magic": "¡Hagamos magia!",
    "maxRows": "Número máximo de filas: {maxRows}",
    "removeFile": "Eliminar archivo | Eliminar archivos"
  },
  "fr": {
    "allFormatsAllowed": "Tous les formats sont autorisés",
    "formatError": "Le format de ce fichier n'est pas autorisé | Les formats de ces fichiers ne sont pas autorisés",
    "formats": "Formats autorisés: {formats}",
    "instructions": "Cliquez ici ou déposez votre fichier",
    "loadingFile": "Chargement du fichier...",
    "magic": "Commençons la magie !",
    "maxRows": "Nombre de lignes max: {maxRows}",
    "removeFile": "Supprimer le fichier | Supprimer les fichiers"
  }
}
</i18n>
